import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment/moment";

import { PERMISSIONS } from "../../constants";
import { isAdmin } from "../../functions";
import { FILED_RANGES } from "./useFiledSearch";

// "Personalizado" sits in the same group as the presets: picking a period is
// one decision, so it reads as one control rather than a button row plus an
// unlabelled switch.
const RANGE_BUTTONS = [
  { key: FILED_RANGES.ALL, label: "Todos" },
  { key: FILED_RANGES.TODAY, label: "Hoy" },
  { key: FILED_RANGES.WEEK, label: "Semana" },
  { key: FILED_RANGES.MONTH, label: "Mes" },
  { key: FILED_RANGES.LAST_MONTH, label: "Mes pasado" },
  { key: FILED_RANGES.CUSTOM, label: "Personalizado" },
];

const FiledItemFilter = ({
  handleFilter,
  hasButton,
  search = "",
  onSearchChange,
  activeRange,
  correspondences,
  filedType = "all",
  onFiledTypeChange,
  onRefresh,
  busy,
  statusMessage,
}) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  // Tracks that the user picked "Personalizado" but hasn't applied dates yet,
  // so the pickers stay open between selecting and applying.
  const [customMode, setCustomMode] = useState(activeRange === FILED_RANGES.CUSTOM);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const handleRangeChange = (_evt, value) => {
    if (!value) return; // ignore deselecting the active range
    if (value === FILED_RANGES.CUSTOM) {
      setCustomMode(true);
      return; // wait for "Aplicar" — the dates aren't chosen yet
    }
    setCustomMode(false);
    handleFilter(value);
  };

  const canConfig = isAdmin(user);
  const canCreate = user.permissions.includes(PERMISSIONS.FILED.CREATE);
  const types = correspondences || [];

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, m: { xs: 1, sm: 2 } }}>
      <Stack spacing={1.5}>
        {/* Search is the primary control, so it leads and takes the full row. */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title="Volver">
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ border: "thin solid", borderColor: "divider", flexShrink: 0 }}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <TextField
              id="filterbyFild"
              size="small"
              fullWidth
              label="Buscar radicados"
              placeholder="Número, asunto, remitente o texto del documento"
              autoComplete="off"
              value={search}
              onChange={(evt) => onSearchChange?.(evt.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <Tooltip title="Limpiar búsqueda">
                      <IconButton size="small" onClick={() => onSearchChange?.("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Stack>
          {hasButton && canCreate && (
            <Button
              variant="contained"
              onClick={() => navigate("/filed")}
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              Radicar documento
            </Button>
          )}
        </Stack>

        <Divider />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0, alignSelf: "center" }}
          >
            Periodo
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={customMode ? FILED_RANGES.CUSTOM : activeRange}
            onChange={handleRangeChange}
            sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { textTransform: "none", px: 1.5 } }}
          >
            {RANGE_BUTTONS.map(({ key, label }) => (
              <ToggleButton key={key} value={key}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {types.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="filed-type-filter-label">Tipo</InputLabel>
              <Select
                labelId="filed-type-filter-label"
                label="Tipo"
                value={filedType}
                onChange={(evt) => onFiledTypeChange?.(evt.target.value)}
              >
                <MenuItem value="all">Todos los tipos</MenuItem>
                {types.map((corresp) => (
                  <MenuItem key={corresp.correspondenceId} value={corresp.correspondenceId}>
                    {corresp.subserieName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Stack direction="row" spacing={0.5} sx={{ ml: { sm: "auto" }, alignSelf: "center" }}>
            <Tooltip title="Recargar">
              <span>
                <IconButton size="small" color="primary" onClick={onRefresh} disabled={busy}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {canConfig && (
              <Tooltip title="Configuración de radicados">
                <IconButton size="small" onClick={() => navigate("/filedlist/config")}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {customMode && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <DatePicker
              label="Desde"
              value={moment(startDate)}
              onChange={(momt) => setStartDate(momt)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <DatePicker
              label="Hasta"
              value={moment(endDate)}
              onChange={(momt) => setEndDate(momt)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <Button
              variant="outlined"
              onClick={() => handleFilter(FILED_RANGES.CUSTOM, startDate, endDate)}
              sx={{ flexShrink: 0 }}
            >
              Aplicar
            </Button>
          </Stack>
        )}

        {statusMessage && (
          <Typography variant="caption" color={busy ? "primary" : "text.secondary"}>
            {statusMessage}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default FiledItemFilter;
